import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import history from '../history';
import AuthContext from '../context/AuthContext';

import Container from 'react-bootstrap/esm/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';

const CLASSIFICATION_LABELS = {
    1: 'excellent',
    2: 'good',
    3: 'fair',
    4: 'poor',
};

const SUBJECTIVE_OPTIONS = [1, 2, 3, 4];
const TOTAL_PATIENTS = 1000;

const getClassificationVariant = (value) => {
    switch (CLASSIFICATION_LABELS[value]) {
        case 'excellent':
            return 'success';
        case 'good':
            return 'primary';
        case 'fair':
            return 'warning';
        case 'poor':
            return 'danger';
        default:
            return 'secondary';
    }
};

const buildImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `http://breloai-backend.inesctec.pt/${path}`;
};

const extractContourPoints = (imageData, prefix, startIndex, endIndex, endpointKeyX, endpointKeyY, midpointX, midpointY) => {
    const points = [];

    if (imageData?.[midpointX] != null && imageData?.[midpointY] != null) {
        points.push({ x: imageData[midpointX], y: imageData[midpointY] });
    }

    for (let index = startIndex; index <= endIndex; index += 1) {
        const xKey = `${prefix}_${index}_x`;
        const yKey = `${prefix}_${index}_y`;

        if (imageData?.[xKey] != null && imageData?.[yKey] != null) {
            points.push({ x: imageData[xKey], y: imageData[yKey] });
        }
    }

    if (imageData?.[endpointKeyX] != null && imageData?.[endpointKeyY] != null) {
        points.push({ x: imageData[endpointKeyX], y: imageData[endpointKeyY] });
    }

    return points;
};

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const getDynamicPointRadius = (imageWidth, imageHeight) => {
    const minDimension = Math.min(imageWidth || 1, imageHeight || 1);

    // About 1.2% of the smallest image dimension,
    // but never too tiny or too huge.
    return clamp(Math.round(minDimension * 0.012), 4, 20);
};

const keepPointInsideImage = (point, imageWidth, imageHeight, margin = 0) => {
    if (!point) return null;

    return {
        x: clamp(Number(point.x), margin, Math.max(margin, imageWidth - margin)),
        y: clamp(Number(point.y), margin, Math.max(margin, imageHeight - margin)),
    };
};

const sanitizePatientKeypoints = (patient) => {
    if (!patient) return patient;

    const imageWidth = patient.imageWidth || 1;
    const imageHeight = patient.imageHeight || 1;
    const radius = getDynamicPointRadius(imageWidth, imageHeight);
    const margin = radius;

    const sanitizePointArray = (points = []) =>
        points.map((point) =>
            keepPointInsideImage(point, imageWidth, imageHeight, margin)
        );

    const sanitizeSinglePoint = (point) =>
        keepPointInsideImage(point, imageWidth, imageHeight, margin);

    return {
        ...patient,
        leftContourPoints: sanitizePointArray(patient.leftContourPoints),
        rightContourPoints: sanitizePointArray(patient.rightContourPoints),
        leftNipple: sanitizeSinglePoint(patient.leftNipple),
        rightNipple: sanitizeSinglePoint(patient.rightNipple),
        sternalNotch: sanitizeSinglePoint(patient.sternalNotch),
        scaleMark: sanitizeSinglePoint(patient.scaleMark),
    };
};

const normalizePatient = (rawPatient) => {
    const imageData = rawPatient.image_data || {};

    return sanitizePatientKeypoints({
        id: imageData.id,
        patientId: rawPatient.patient_id,
        institutionalNumber: rawPatient.institutional_number,
        imageUrl: buildImageUrl(imageData.image),
        imageWidth: imageData.image_width || 1,
        imageHeight: imageData.image_height || 1,
        leftContourPoints: extractContourPoints(
            imageData,
            'l_breast_contour',
            2,
            16,
            'left_endpoint_x',
            'left_endpoint_y',
            'left_midpoint_x',
            'left_midpoint_y'
        ),
        rightContourPoints: extractContourPoints(
            imageData,
            'r_breast_contour',
            19,
            33,
            'right_midpoint_x',
            'right_midpoint_y',
            'right_endpoint_x',
            'right_endpoint_y'
        ),
        leftNipple:
            imageData.left_nipple_x != null && imageData.left_nipple_y != null
                ? { x: imageData.left_nipple_x, y: imageData.left_nipple_y }
                : null,
        rightNipple:
            imageData.right_nipple_x != null && imageData.right_nipple_y != null
                ? { x: imageData.right_nipple_x, y: imageData.right_nipple_y }
                : null,
        sternalNotch:
            imageData.sternal_notch_x != null && imageData.sternal_notch_y != null
                ? { x: imageData.sternal_notch_x, y: imageData.sternal_notch_y }
                : null,
        scaleMark:
            imageData.scale_mark_x != null && imageData.scale_mark_y != null
                ? { x: imageData.scale_mark_x, y: imageData.scale_mark_y }
                : null,
        objectiveClassification: rawPatient.objective_classification,
        subjectiveClassification: rawPatient.subjective_classification ?? '',
        oridnalClipClassification: rawPatient.oridnal_clip_classification,
        rawImageData: imageData,
    });
};

const SPLINE_RESOLUTION = 64;

const catmullRom = (p0, p1, p2, p3, t) => {
    const t2 = t * t;
    const t3 = t2 * t;

    return {
        x:
            0.5 *
            ((2 * p1.x) +
                (-p0.x + p2.x) * t +
                (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
                (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y:
            0.5 *
            ((2 * p1.y) +
                (-p0.y + p2.y) * t +
                (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
                (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    };
};

const sampleCatmullRomSpline = (points, resolution = SPLINE_RESOLUTION, closed = false) => {
    if (!points || points.length < 2) return [];

    const sampled = [];

    if (closed) {
        const pts = [points[points.length - 1], ...points, points[0], points[1]];

        for (let i = 1; i < pts.length - 2; i += 1) {
            for (let step = 0; step < resolution; step += 1) {
                const t = step / resolution;
                sampled.push(catmullRom(pts[i - 1], pts[i], pts[i + 1], pts[i + 2], t));
            }
        }

        sampled.push(points[0]);
        return sampled;
    }

    const pts = [points[0], ...points, points[points.length - 1]];

    for (let i = 1; i < pts.length - 2; i += 1) {
        for (let step = 0; step < resolution; step += 1) {
            const t = step / resolution;
            sampled.push(catmullRom(pts[i - 1], pts[i], pts[i + 1], pts[i + 2], t));
        }
    }

    sampled.push(points[points.length - 1]);
    return sampled;
};

const buildSplinePath = (points, closed = false, resolution = SPLINE_RESOLUTION) => {
    const sampledPoints = sampleCatmullRomSpline(points, resolution, closed);

    if (!sampledPoints.length) return '';

    return sampledPoints
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ');
};

const buildContourPayload = (patient) => {
    const payload = {};
    if (patient.leftContourPoints?.length > 0) {
        const [leftMidpoint, ...rest] = patient.leftContourPoints;
        const leftEndpoint = rest.pop();
        const leftBreast = rest;

        payload.left_endpoint_x = Math.round(leftEndpoint.x);
        payload.left_endpoint_y = Math.round(leftEndpoint.y);

        payload.left_midpoint_x = Math.round(leftMidpoint.x);
        payload.left_midpoint_y = Math.round(leftMidpoint.y);

        leftBreast.forEach((point, idx) => {
            const contourIndex = idx + 2;
            payload[`l_breast_contour_${contourIndex}_x`] = Math.round(point.x);
            payload[`l_breast_contour_${contourIndex}_y`] = Math.round(point.y);
        });
    }

    if (patient.rightContourPoints?.length > 0) {
        const [rightMidpoint, ...rest] = patient.rightContourPoints;
        const rightEndpoint = rest.pop();
        const rightBreast = rest;

        payload.right_endpoint_x = Math.round(rightMidpoint.x);
        payload.right_endpoint_y = Math.round(rightMidpoint.y);

        payload.right_midpoint_x = Math.round(rightEndpoint.x);
        payload.right_midpoint_y = Math.round(rightEndpoint.y);

        rightBreast.forEach((point, idx) => {
            const contourIndex = idx + 19;
            payload[`r_breast_contour_${contourIndex}_x`] = Math.round(point.x);
            payload[`r_breast_contour_${contourIndex}_y`] = Math.round(point.y);
        });
    }

    if (patient.leftNipple) {
        payload.left_nipple_x = Math.round(patient.leftNipple.x);
        payload.left_nipple_y = Math.round(patient.leftNipple.y);
    }

    if (patient.rightNipple) {
        payload.right_nipple_x = Math.round(patient.rightNipple.x);
        payload.right_nipple_y = Math.round(patient.rightNipple.y);
    }

    if (patient.scaleMark) {
        payload.scale_mark_x = Math.round(patient.scaleMark.x);
        payload.scale_mark_y = Math.round(patient.scaleMark.y);
    }

    if (patient.sternalNotch) {
        payload.sternal_notch_x = Math.round(patient.sternalNotch.x);
        payload.sternal_notch_y = Math.round(patient.sternalNotch.y);
    }

    payload.p_image_id = patient.id
    payload.patient_id = patient.patientId

    return payload;
};

const applyRefinedContoursToPatient = (patient, refinedData) => {
    if (!patient || !refinedData) return patient;

    const normalizePointArray = (points) => {
        if (!Array.isArray(points)) return [];
        return points.map(([x, y]) => ({
            x: Number(y),
            y: Number(x),
        }));
    };

    const nextPatient = { ...patient };

    if (refinedData.left_contour) {
        nextPatient.leftContourPoints = normalizePointArray(refinedData.left_contour);
    }

    if (refinedData.right_contour) {
        nextPatient.rightContourPoints = normalizePointArray(refinedData.right_contour);
    }

    nextPatient.rawImageData = {
        ...(patient.rawImageData || {}),
        left_contour: refinedData.left_contour,
        right_contour: refinedData.right_contour,
    };

    return nextPatient;
};

const PatientImagePanel = ({ patient, onPointChange, onPointDragEnd, isSavingContours }) => {
    const svgRef = useRef(null);
    const [dragging, setDragging] = useState(null);
    const pointRadius = useMemo(
        () => getDynamicPointRadius(patient.imageWidth, patient.imageHeight),
        [patient.imageWidth, patient.imageHeight]
    );

    const leftContourPath = useMemo(
        () => buildSplinePath(patient.leftContourPoints),
        [patient.leftContourPoints]
    );

    const rightContourPath = useMemo(
        () => buildSplinePath(patient.rightContourPoints),
        [patient.rightContourPoints]
    );

    const getSvgPoint = useCallback(
        (event) => {
            const svg = svgRef.current;
            if (!svg) return null;

            const point = svg.createSVGPoint();
            const clientX = event.clientX ?? event.touches?.[0]?.clientX;
            const clientY = event.clientY ?? event.touches?.[0]?.clientY;

            if (clientX == null || clientY == null) return null;

            point.x = clientX;
            point.y = clientY;

            const ctm = svg.getScreenCTM();
            if (!ctm) return null;

            const transformed = point.matrixTransform(ctm.inverse());

            return {
                x: clamp(transformed.x, pointRadius, Math.max(pointRadius, patient.imageWidth - pointRadius)),
                y: clamp(transformed.y, pointRadius, Math.max(pointRadius, patient.imageHeight - pointRadius)),
            };
        },
        [patient.imageWidth, patient.imageHeight, pointRadius]
    );

    const startDragging = (side, index = null) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging({ side, index });
    };

    useEffect(() => {
        if (!dragging) return undefined;

        const handleMove = (event) => {
            if (event.cancelable) {
                event.preventDefault();
            }

            const point = getSvgPoint(event);
            if (!point) return;

            onPointChange?.(dragging.side, dragging.index, point);
        };

        const handleUp = () => {
            setDragging(null);
            onPointDragEnd?.();
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleUp);

        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
        };
    }, [dragging, getSvgPoint, onPointChange, onPointDragEnd]);

    return (
        <div
            className="shadow"
            style={{
                position: 'relative',
                width: '85%',
                height: '85%',
                borderRadius: 16,
                overflow: 'hidden',
                backgroundColor: '#fff',
            }}
        >
            <svg
                ref={svgRef}
                viewBox={`0 0 ${patient.imageWidth} ${patient.imageHeight}`}
                preserveAspectRatio="xMidYMid meet"
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    touchAction: 'none',
                    cursor: dragging ? 'grabbing' : 'default',
                    userSelect: 'none',
                }}
            >
                <image
                    href={patient.imageUrl}
                    x="0"
                    y="0"
                    width={patient.imageWidth}
                    height={patient.imageHeight}
                    preserveAspectRatio="xMidYMid meet"
                />

                <path
                    d={leftContourPath}
                    fill="rgba(13,110,253,0.08)"
                    stroke="#0d6efd"
                    strokeWidth={4}
                    strokeDasharray="8 4"
                />

                <path
                    d={rightContourPath}
                    fill="rgba(220,53,69,0.08)"
                    stroke="#dc3545"
                    strokeWidth={4}
                    strokeDasharray="8 4"
                />

                {patient.leftContourPoints.map((point, index) => (
                    <circle
                        key={`left-${index}`}
                        cx={point.x}
                        cy={point.y}
                        r={pointRadius}
                        fill="#0d6efd"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.18)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('left', index)}
                        onTouchStart={startDragging('left', index)}
                    />
                ))}

                {patient.rightContourPoints.map((point, index) => (
                    <circle
                        key={`right-${index}`}
                        cx={point.x}
                        cy={point.y}
                        r={pointRadius}
                        fill="#dc3545"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.18)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('right', index)}
                        onTouchStart={startDragging('right', index)}
                    />
                ))}

                {patient.leftNipple && (
                    <circle
                        cx={patient.leftNipple.x}
                        cy={patient.leftNipple.y}
                        r={pointRadius}
                        fill="cyan"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.15)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('leftNipple')}
                        onTouchStart={startDragging('leftNipple')}
                    />
                )}

                {patient.rightNipple && (
                    <circle
                        cx={patient.rightNipple.x}
                        cy={patient.rightNipple.y}
                        r={pointRadius}
                        fill="magenta"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.15)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('rightNipple')}
                        onTouchStart={startDragging('rightNipple')}
                    />
                )}

                {patient.sternalNotch && (
                    <circle
                        cx={patient.sternalNotch.x}
                        cy={patient.sternalNotch.y}
                        r={pointRadius}
                        fill="orange"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.15)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('sternalNotch')}
                        onTouchStart={startDragging('sternalNotch')}
                    />
                )}

                {patient.scaleMark && (
                    <circle
                        cx={patient.scaleMark.x}
                        cy={patient.scaleMark.y}
                        r={pointRadius}
                        fill="orange"
                        stroke="white"
                        strokeWidth={Math.max(1.5, pointRadius * 0.15)}
                        style={{ cursor: 'grab' }}
                        onMouseDown={startDragging('scaleMark')}
                        onTouchStart={startDragging('scaleMark')}
                    />
                )}
            </svg>

            {isSavingContours && (
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        background: 'rgba(255,255,255,0.95)',
                        borderRadius: 10,
                        padding: '6px 10px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                        fontSize: 13,
                        zIndex: 10,
                    }}
                >
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving contour...
                </div>
            )}
        </div>
    );
};

const ObjectivePanel = ({ patient, onRerunObjective, isRefreshing }) => {
    return (
        <Card className="h-100 shadow border-1">
            <Card.Body className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <Card.Title className="mb-1">Objective classification</Card.Title>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span>Original Classification</span>
                    <Badge bg={getClassificationVariant(patient.objectiveClassification)} className="text-uppercase px-3 py-2">
                        {CLASSIFICATION_LABELS[patient.objectiveClassification] || 'not available'}
                    </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-start mb-2">
                    <span>Ordinal CLIP Classification</span>
                    <Badge bg={getClassificationVariant(patient.oridnalClipClassification)} className="text-uppercase px-3 py-2">
                        {CLASSIFICATION_LABELS[patient.oridnalClipClassification] || 'not available'}
                    </Badge>
                </div>

                <div className="mt-auto d-grid">
                    <Button
                        style={{ backgroundColor: '#00A776', borderColor: '#003828', borderWidth: 1 }}
                        onClick={onRerunObjective}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Running classification...
                            </>
                        ) : (
                            'Re-run objective classification'
                        )}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};


const SubjectivePanel = ({
    patient,
    subjectiveValue,
    onChange,
    onSubmit,
    isSaving,
}) => {
    return (
        <Card className="h-100 shadow border-1">
            <Card.Body className="d-flex flex-column">
                <div className="mb-3">
                    <Card.Title className="mb-1">Subjective classification</Card.Title>
                </div>

                <Form className="d-flex flex-column flex-grow-1">
                    <Form.Group>
                        <Form.Select value={subjectiveValue} onChange={(event) => onChange(event.target.value)}>
                            <option value="">Select classification</option>
                            {SUBJECTIVE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {CLASSIFICATION_LABELS[option].charAt(0).toUpperCase() + CLASSIFICATION_LABELS[option].slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <div className="mt-auto d-grid gap-2 pt-3">
                        <Button
                            style={{ backgroundColor: '#00A776', borderColor: '#003828', borderWidth: 1 }}
                            onClick={onSubmit}
                            disabled={!subjectiveValue || isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving classification...
                                </>
                            ) : (
                                'Submit subjective classification'
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

const PatientReviewPage = () => {
    const authContext = useContext(AuthContext) || {};
    const authTokens = authContext.authTokens;
    const url = authContext.url;

    const [patients, setPatients] = useState([]);
    const [totalPatients, setTotalPatients] = useState(TOTAL_PATIENTS);
    const [isLoadingPatients, setIsLoadingPatients] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [subjectiveDrafts, setSubjectiveDrafts] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isRefreshingObjective, setIsRefreshingObjective] = useState(false);
    const [isSavingContours, setIsSavingContours] = useState(false);
    const [isRefiningKeypoints, setIsRefiningKeypoints] = useState(false);
    const [patientNumberInput, setPatientNumberInput] = useState('');
    const [institutionalNumberInput, setInstitutionalNumberInput] = useState('');

    const currentPatientRef = useRef(null);

    const currentPatient = patients[currentIndex] || null;

    useEffect(() => {
        currentPatientRef.current = currentPatient;
    }, [currentPatient]);

    const reviewedCount = useMemo(
        () =>
            patients.filter(
                (patient) =>
                    patient.subjectiveClassification !== '' &&
                    patient.subjectiveClassification !== null
            ).length,
        [patients]
    );

    const unresolvedCount = useMemo(
        () =>
            patients.filter(
                (patient) =>
                    patient.subjectiveClassification === '' ||
                    patient.subjectiveClassification === null
            ).length,
        [patients]
    );

    const handleRefineKeypoints = async () => {
        if (!currentPatient) return;

        try {
            setIsRefiningKeypoints(true);
            setLoadError('');

            let response = await fetch('https://breloai-backend.inesctec.pt/api/patient/' + currentPatient.patientId + '/' + currentPatient.id + '/refineKeyPoints', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authTokens?.access,
                },
            })

            if (!response.ok) {
                throw new Error('Failed to refine keypoints.');
            }

            const refinedData = await response.json();

            setPatients((prev) =>
                prev.map((patient) =>
                    patient.id === currentPatient.id
                        ? applyRefinedContoursToPatient(patient, refinedData)
                        : patient
                )
            );

            currentPatientRef.current = applyRefinedContoursToPatient(
                currentPatientRef.current,
                refinedData
            );
        } catch (error) {
            setLoadError(error.message || 'Failed to refine keypoints.');
        } finally {
            setIsRefiningKeypoints(false);
        }
        const latestPatient = currentPatientRef.current;
        if (!latestPatient) return;
        const contourPayload = buildContourPayload(latestPatient);
        try {
            setIsSavingContours(true);
            setLoadError('');

            const response = await fetch(
                'https://breloai-backend.inesctec.pt/api/patient/' + latestPatient.patientId + '/' + latestPatient.id + '/updateKeyPoints',
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(authTokens ? { Authorization: `Bearer ${authTokens.access || authTokens}` } : {}),
                    },
                    credentials: 'include',
                    body: JSON.stringify(contourPayload),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to save contour points.');
            }
        } catch (error) {
            setLoadError(error.message || 'Failed to save contour points.');
        } finally {
            setIsSavingContours(false);
        }
    };

    const progressDenominator = totalPatients || patients.length || 1;
    const progressValue = (reviewedCount / progressDenominator) * 100;

    useEffect(() => {
        const fetchPatients = async () => {
            try {
                setIsLoadingPatients(true);
                setLoadError('');

                const response = await fetch(`https://breloai-backend.inesctec.pt/api/cinderella_classifications`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(authTokens ? { Authorization: `Bearer ${authTokens.access || authTokens}` } : {}),
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error('Unable to load patients from backend.');
                }

                const payload = await response.json();
                const rawPatients = Array.isArray(payload)
                    ? payload
                    : payload.results || payload.patients || [];

                const normalizedPatients = rawPatients.map(normalizePatient);

                const sortedPatients = [
                    ...normalizedPatients.filter(
                        (patient) =>
                            patient.subjectiveClassification === '' ||
                            patient.subjectiveClassification === null
                    ),
                    ...normalizedPatients.filter(
                        (patient) =>
                            patient.subjectiveClassification !== '' &&
                            patient.subjectiveClassification !== null
                    ),
                ];

                setPatients(sortedPatients);
                setTotalPatients(
                    typeof payload.count === 'number'
                        ? payload.count
                        : rawPatients.length || TOTAL_PATIENTS
                );
                setCurrentIndex(0);
            } catch (error) {
                setLoadError(error.message || 'Unable to load patients.');
            } finally {
                setIsLoadingPatients(false);
            }
        };

        fetchPatients();
    }, []);

    const currentSubjectiveValue = currentPatient
        ? subjectiveDrafts[currentPatient.id] ?? currentPatient.subjectiveClassification ?? ''
        : '';

    const handleSubjectiveChange = (value) => {
        if (!currentPatient) return;

        setSubjectiveDrafts((prev) => ({
            ...prev,
            [currentPatient.id]: value,
        }));
    };

    const handleSaveSubjective = async () => {
        if (!currentPatient || currentSubjectiveValue === '') return;

        try {
            setIsSaving(true);
            setLoadError('');

            const response = await fetch(
                `https://breloai-backend.inesctec.pt/api/patient/${currentPatient.patientId}/${currentPatient.id}/classification`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(authTokens ? { Authorization: `Bearer ${authTokens.access || authTokens}` } : {}),
                    },
                    credentials: 'include',
                    body: Number(currentSubjectiveValue),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to save subjective classification.');
            }

            const savedPatientId = currentPatient.id;

            setPatients((prev) =>
                prev.map((patient) =>
                    patient.id === savedPatientId
                        ? { ...patient, subjectiveClassification: Number(currentSubjectiveValue) }
                        : patient
                )
            );

            setSubjectiveDrafts((prev) => {
                const next = { ...prev };
                delete next[savedPatientId];
                return next;
            });

            setCurrentIndex((prev) => Math.min(prev + 1, Math.max(patients.length - 1, 0)));
        } catch (error) {
            setLoadError(error.message || 'Failed to save subjective classification.');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePointChange = useCallback((side, index, newPoint) => {
        if (!currentPatientRef.current) return;

        setPatients((prev) =>
            prev.map((patient) => {
                if (patient.id !== currentPatientRef.current.id) return patient;

                let updatedPatient = patient;

                if (side === 'left') {
                    const updatedPoints = [...patient.leftContourPoints];
                    updatedPoints[index] = newPoint;
                    updatedPatient = {
                        ...patient,
                        leftContourPoints: updatedPoints,
                    };
                } else if (side === 'right') {
                    const updatedPoints = [...patient.rightContourPoints];
                    updatedPoints[index] = newPoint;
                    updatedPatient = {
                        ...patient,
                        rightContourPoints: updatedPoints,
                    };
                } else if (side === 'leftNipple') {
                    updatedPatient = {
                        ...patient,
                        leftNipple: newPoint,
                    };
                } else if (side === 'rightNipple') {
                    updatedPatient = {
                        ...patient,
                        rightNipple: newPoint,
                    };
                } else if (side === 'sternalNotch') {
                    updatedPatient = {
                        ...patient,
                        sternalNotch: newPoint,
                    };
                } else if (side === 'scaleMark') {
                    updatedPatient = {
                        ...patient,
                        scaleMark: newPoint,
                    };
                }

                const sanitizedPatient = sanitizePatientKeypoints(updatedPatient);
                currentPatientRef.current = sanitizedPatient;
                return sanitizedPatient;
            })
        );
    }, []);

    const handlePointDragEnd = useCallback(async () => {
        const latestPatient = currentPatientRef.current;
        if (!latestPatient) return;
        const contourPayload = buildContourPayload(latestPatient);
        try {
            setIsSavingContours(true);
            setLoadError('');

            const response = await fetch(
                'https://breloai-backend.inesctec.pt/api/patient/' + latestPatient.patientId + '/' + latestPatient.id + '/updateKeyPoints',
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(authTokens ? { Authorization: `Bearer ${authTokens.access || authTokens}` } : {}),
                    },
                    credentials: 'include',
                    body: JSON.stringify(contourPayload),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to save contour points.');
            }
        } catch (error) {
            setLoadError(error.message || 'Failed to save contour points.');
        } finally {
            setIsSavingContours(false);
        }
    }, [url, authTokens]);

    const handleRerunObjective = async () => {
        if (!currentPatient) return;

        try {
            setIsRefreshingObjective(true);
            setLoadError('');

            let response = await fetch('https://breloai-backend.inesctec.pt/api/patient/' + currentPatient.patientId + '/' + currentPatient.id + '/classification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authTokens?.access,
                },
                body: JSON.stringify({ mode: 'objective' }),
            })
            let data = await response.json()

            setPatients((prev) =>
                prev.map((patient) =>
                    patient.id === currentPatient.id
                        ? {
                            ...patient,
                            objectiveClassification: data.aesthetic_classification,
                            oridnalClipClassification: data.aesthetic_classification_fabio
                        }
                        : patient
                )
            );
        } finally {
            setIsRefreshingObjective(false);
        }
    };

    const goToPreviousPatient = () => {
        setLoadError('');
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    };

    const goToNextPatient = () => {
        setLoadError('');
        setCurrentIndex((prev) => Math.min(prev + 1, patients.length - 1));
    };

    const goToInstitutionalNumber = () => {
        const searchValue = institutionalNumberInput.trim();
        if (!searchValue) return;

        const index = patients.findIndex(
            (patient) => String(patient.institutionalNumber).trim() === searchValue
        );

        if (index >= 0) {
            setCurrentIndex(index);
            setLoadError('');
        } else {
            setLoadError(`Patient with institutional number "${searchValue}" was not found.`);
        }
    };

    if (!isLoadingPatients && !loadError && !currentPatient) {
        return (
            <Container className="content-container mt-4">
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <h3 className="mb-3">All patients reviewed</h3>
                        <p className="text-muted mb-4">The queue is empty. All patients have been completed.</p>
                        <Button variant="primary" onClick={() => history.push('/')}>
                            Back to dashboard
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <div
            style={{
                height: 'calc(100vh - 90px)',
                width: '95vw',
                margin: '0 auto',
            }}
        >
            <div className="d-flex justify-content-center">
                <Card className="shadow border-1 mb-2 w-100">
                    <Card.Body>
                        <Row className="align-items-center">
                            <Col lg={4}>
                                <div className="small text-muted">Review progress: {reviewedCount} reviewed · {unresolvedCount} unresolved</div>
                                <div className="d-flex align-items-center gap-3">
                                    <h5 className="mb-0">
                                        {currentPatient
                                            ? `Patient ${currentPatient.institutionalNumber}`
                                            : 'Patient review'}
                                    </h5>
                                    <Badge bg="dark">
                                        {patients.length > 0 ? currentIndex + 1 : 0} / {patients.length}
                                    </Badge>
                                </div>
                            </Col>

                            <Col lg={5}>
                                <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span>{reviewedCount} reviewed</span>
                                    <span>{progressDenominator} total</span>
                                </div>
                                <ProgressBar now={progressValue} label={`${Math.round(progressValue)}%`} />
                            </Col>

                            <Col lg={3}>
                                <div className="d-flex justify-content-lg-end gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goToPreviousPatient}
                                        disabled={currentIndex === 0 || patients.length === 0}
                                    >
                                        Previous patient
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goToNextPatient}
                                        disabled={currentIndex === patients.length - 1 || patients.length === 0}
                                    >
                                        Next patient
                                    </Button>
                                    <Form.Group style={{ maxWidth: 120 }}>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={patients.length}
                                            placeholder="#"
                                            value={patientNumberInput}
                                            onChange={(e) => setPatientNumberInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const value = Number(patientNumberInput);
                                                    if (!Number.isNaN(value) && patientNumberInput !== '') {
                                                        const index = Math.max(
                                                            0,
                                                            Math.min(value - 1, patients.length - 1)
                                                        );
                                                        setCurrentIndex(index);
                                                        setLoadError('');
                                                    }
                                                }
                                            }}
                                        />
                                    </Form.Group>
                                    <Form.Group style={{ maxWidth: 180 }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Institutional #"
                                            value={institutionalNumberInput}
                                            onChange={(e) => setInstitutionalNumberInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    goToInstitutionalNumber();
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={goToInstitutionalNumber}
                                            disabled={!institutionalNumberInput.trim()}
                                        >
                                            Go
                                        </Button>
                                    </Form.Group>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </div>

            {loadError ? (
                <Alert variant="danger" className="mb-0">
                    {loadError}
                </Alert>
            ) : isLoadingPatients ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="d-flex align-items-center justify-content-center py-5">
                        <div className="text-center">
                            <Spinner animation="border" className="mb-3" />
                            <div>Loading patients from backend...</div>
                        </div>
                    </Card.Body>
                </Card>
            ) : currentPatient ? (
                <div className="d-flex flex-column flex-lg-row gap-3" style={{ minHeight: '100%' }}>
                    {/* Painel da imagem */}
                    <div
                        className="d-flex align-items-center justify-content-center flex-grow-1"
                        style={{
                            minWidth: 0,
                            minHeight: '400px',
                            flex: '1 1 auto'
                        }}
                    >
                        <div style={{ width: '100%', height: '100%' }}>
                            <PatientImagePanel
                                patient={currentPatient}
                                onPointChange={handlePointChange}
                                onPointDragEnd={handlePointDragEnd}
                                isSavingContours={isSavingContours}
                            />
                        </div>
                    </div>

                    {/* Painel lateral */}
                    <div
                        className="d-flex flex-column align-items-center"
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            minWidth: '280px',
                            flexShrink: 0,
                        }}
                    >
                        <div className="w-100 d-flex justify-content-center mt-3 mt-lg-4 mb-2">
                            <Button
                                className="w-75"
                                style={{
                                    backgroundColor: '#00A776',
                                    borderColor: '#003828',
                                    borderWidth: 1,
                                }}
                                onClick={handleRefineKeypoints}
                                disabled={isRefiningKeypoints || !currentPatient}
                            >
                                {isRefiningKeypoints ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Refining keypoints...
                                    </>
                                ) : (
                                    'Refine keypoints'
                                )}
                            </Button>
                        </div>

                        {/* Objective e Subjective alinhados verticalmente */}
                        <div className="w-100 d-flex flex-column align-items-center gap-3 mt-3 mt-lg-4">
                            <ObjectivePanel
                                patient={currentPatient}
                                onRerunObjective={handleRerunObjective}
                                isRefreshing={isRefreshingObjective}
                            />

                            <SubjectivePanel
                                patient={currentPatient}
                                subjectiveValue={currentSubjectiveValue}
                                onChange={handleSubjectiveChange}
                                onSubmit={handleSaveSubjective}
                                isSaving={isSaving}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default withRouter(PatientReviewPage);
